// src/game-system/races/dragonborn.js
import { BaseRace } from './baseRace';

const DRACONIC_ANCESTRIES = {
  black: {
    element: "Acid",
    damage: "acid",
    habitat: "Swamp",
    description: "Cunning and stealthy dragons of the marshlands"
  },
  blue: {
    element: "Lightning",
    damage: "lightning",
    habitat: "Desert",
    description: "Proud and territorial dragons of the desert"
  },
  brass: {
    element: "Fire",
    damage: "fire",
    habitat: "Desert",
    description: "Talkative and friendly dragons of the warm sands"
  },
  bronze: {
    element: "Lightning",
    damage: "lightning",
    habitat: "Coastal",
    description: "Justice-minded dragons of the coastline"
  },
  copper: {
    element: "Acid",
    damage: "acid",
    habitat: "Hills",
    description: "Witty and humorous dragons of the hills"
  },
  gold: {
    element: "Fire",
    damage: "fire",
    habitat: "Mountains",
    description: "Wise and regal dragons of the mountains"
  },
  green: {
    element: "Poison",
    damage: "poison",
    habitat: "Forest",
    description: "Deceptive dragons of the deep forests"
  },
  red: {
    element: "Fire",
    damage: "fire",
    habitat: "Mountains",
    description: "Proud and powerful dragons of the mountains"
  },
  silver: {
    element: "Cold",
    damage: "cold",
    habitat: "Mountains",
    description: "Noble and helpful dragons of the peaks"
  },
  white: {
    element: "Cold",
    damage: "cold",
    habitat: "Arctic",
    description: "Fierce and territorial dragons of the frozen wastes"
  }
};

export class Dragonborn extends BaseRace {
  /**
   * @param {string} ancestry - Draconic ancestry type
   */
  constructor(ancestry) {
    if (!DRACONIC_ANCESTRIES[ancestry]) {
      throw new Error(`Invalid draconic ancestry: ${ancestry}`);
    }

    const config = {
      name: "Dragonborn",
      description: `A proud warrior race descended from dragons of ${ancestry} ancestry`,
      attributeBonuses: {
        strength: 2,
        charisma: 1
      },
      traits: [
        {
          name: "Draconic Ancestry",
          description: `You have ${ancestry} dragon ancestry`,
          effect: {
            type: "passive",
            mechanics: `Resistance to ${DRACONIC_ANCESTRIES[ancestry].damage} damage`
          }
        },
        {
          name: "Breath Weapon",
          description: `You can exhale destructive ${DRACONIC_ANCESTRIES[ancestry].element}`,
          effect: {
            type: "active",
            mechanics: `Action to exhale ${DRACONIC_ANCESTRIES[ancestry].element} in a 15-foot cone. 2d6 damage, DC = 8 + CON modifier + proficiency bonus`,
            limitations: "Once per short rest"
          }
        }
      ],
      cultures: [
        {
          name: "Clan Warrior",
          description: "Traditional warrior society focused on honor and combat",
          bonusSkills: ["Athletics", "Intimidation"],
          startingEquipment: ["Ceremonial weapon", "Clan insignia", "Battle trophy"],
          traditions: [
            {
              name: "Dragon's Honor",
              benefit: "Advantage on Charisma checks related to honor and reputation"
            }
          ]
        },
        {
          name: "Dragon Temple",
          description: "Religious society devoted to draconic heritage",
          bonusSkills: ["Religion", "History"],
          startingEquipment: ["Religious symbol", "Prayer book", "Incense"],
          traditions: [
            {
              name: "Dragon's Wisdom",
              benefit: "Advantage on History checks related to dragons"
            }
          ]
        }
      ],
      size: {
        category: "Medium",
        height: { min: 180, max: 200 },
        weight: { min: 95, max: 130 }
      },
      speed: 30,
      languages: {
        known: ["Common", "Draconic"],
        bonus: 0
      }
    };

    super(config);
    this.ancestry = ancestry;
  }

  /**
   * Get ancestry-specific features
   * @returns {Object}
   */
  getAncestryFeatures() {
    return DRACONIC_ANCESTRIES[this.ancestry];
  }
}